import {
  faArchive,
  faBadgeCheck,
  faBriefcase,
  faEnvelope,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';

import { AvatarV2 as Avatar, ContextMenu, Pill, Tab } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useTableSelection } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';
import { getIconForAction, SELECTED_PERSONS } from '@feathr/hooks';

import ConfirmArchivePeopleModal from '../ConfirmArchivePeopleModal';
import type { TArchiveAction } from '../types';
import PersonDetail from './PersonDetail';

import * as styles from './PersonPage.css';

function PersonPage(): JSX.Element {
  const { Persons } = useStore();
  const { t } = useTranslation();
  const history = useHistory();
  const { accountId, personId } = useParams<{ accountId: string; personId: string }>();
  const person = Persons.get(personId);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState<boolean>(false);

  const [modalAction, setModalAction] = useState<TArchiveAction>('archive');

  const selection = useTableSelection(SELECTED_PERSONS);

  useEffect(() => {
    selection.setSelected([personId]);
  }, [personId]);

  if (!person.isPending && person.get('companies') && !Array.isArray(person.get('companies'))) {
    person.set({ companies: ([] as string[]).concat(person.get('companies')) });
  }

  const tabs = [
    <Tab key={'activity'} link={'activity'} title={t('Activity feed')} />,
    <Tab key={'profile'} link={'profile'} title={t('Profile')} />,
    <Tab key={'custom'} link={'custom-fields'} title={t('Custom fields')} />,
    <Tab key={'subscriptions'} link={'subscriptions'} title={t('Subscriptions')} />,
  ];

  const email = person.get('email');
  const occupation = person.get('occupation');
  const isArchived = person.isArchived;

  const description = (function (): JSX.Element | null {
    if (email || occupation || person.reachable) {
      return (
        <div>
          {(email || occupation) && (
            <div className={styles.descriptionElements}>
              {email && (
                <span>
                  <FontAwesomeIcon icon={faEnvelope} />
                  {email}
                </span>
              )}
              {occupation && (
                <span>
                  <FontAwesomeIcon icon={faBriefcase} />
                  {occupation}
                </span>
              )}
            </div>
          )}
        </div>
      );
    }

    return null;
  })();

  const handleOpenArchiveModal = (actionType: TArchiveAction): void => {
    setModalAction(actionType);
    setIsArchiveModalOpen(true);
  };

  const contextMenu = (
    <ContextMenu buttonType={'icon-outlined'} iconName={'ellipsis'}>
      <ContextMenu.Item
        onClick={(): void => handleOpenArchiveModal(isArchived ? 'restore' : 'archive')}
        prefix={getIconForAction(isArchived ? 'return' : 'archive')}
        theme={isArchived ? undefined : 'danger'}
      >
        {isArchived ? t('Restore') : t('Archive')}
      </ContextMenu.Item>
    </ContextMenu>
  );

  const handleCloseArchiveModal = (): void => {
    setIsArchiveModalOpen(false);
    selection.clearSelection();
  };

  const handleActionComplete = (): void => {
    if (modalAction === 'archive') {
      history.push(`/${accountId}/data/explore/all/people`);
    }
  };

  const getChips = (): JSX.Element | undefined => {
    return person.reachable || person.isArchived ? (
      <div className={styles.chips}>
        {person.reachable && (
          <Pill prefix={<FontAwesomeIcon icon={faBadgeCheck} />} theme={'green'}>
            {t('Reachable')}
          </Pill>
        )}
        {person.isArchived && (
          <Pill prefix={<FontAwesomeIcon icon={faArchive} />} theme={'red'}>
            {t('Archived')}
          </Pill>
        )}
      </div>
    ) : undefined;
  };

  return (
    <Page
      actions={contextMenu}
      ChipsComponent={getChips()}
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={description}
      loading={person.isPending}
      prefix={
        <Avatar
          className={styles.avatar}
          name={person.get('name')}
          placeholder={person.get('placeholder')}
          size={'large'}
        />
      }
      tabs={tabs}
      title={person.name}
      width={'wide'}
    >
      <PersonDetail person={person} />
      {isArchiveModalOpen && (
        <ConfirmArchivePeopleModal
          action={modalAction}
          onClose={handleCloseArchiveModal}
          onSuccess={handleActionComplete}
          opened={isArchiveModalOpen}
          selection={selection}
        />
      )}
    </Page>
  );
}

export default observer(PersonPage);
